import React, { useState, useEffect } from 'react';
import { X, Gift, Mail, Sparkles } from 'lucide-react';

const EmailPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Check if user has already seen the popup
    const hasSeenPopup = localStorage.getItem('adanos_popup_seen');
    
    if (!hasSeenPopup) {
      // Show popup after 3 seconds
      const timer = setTimeout(() => {
        setIsOpen(true);
        setIsAnimating(true);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsOpen(false);
      localStorage.setItem('adanos_popup_seen', 'true');
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) return;

    try {
      // Save email to backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscribers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        setIsSubmitted(true);
        localStorage.setItem('adanos_popup_seen', 'true');
        localStorage.setItem('adanos_discount_code', 'WELCOME10');
      }
    } catch (error) {
      // Even if API fails, show success (we can still use the discount)
      setIsSubmitted(true);
      localStorage.setItem('adanos_popup_seen', 'true');
      localStorage.setItem('adanos_discount_code', 'WELCOME10');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div 
        className={`relative w-full max-w-lg transform transition-all duration-300 ${
          isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
      >
        <div className="relative overflow-hidden rounded-3xl shadow-2xl">
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-gradient-to-br from-red-600 via-red-700 to-red-900">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
              <div className="absolute bottom-0 right-0 w-60 h-60 bg-white rounded-full translate-x-1/2 translate-y-1/2" />
              <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-yellow-400 rounded-full -translate-x-1/2 -translate-y-1/2" />
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
            aria-label="Close popup"
          >
            <X className="w-5 h-5 text-white" />
          </button>

          {/* Content */}
          <div className="relative p-8 md:p-10">
            {!isSubmitted ? (
              <>
                {/* Icon */}
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    <div className="w-20 h-20 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg animate-bounce">
                      <Gift className="w-10 h-10 text-red-700" />
                    </div>
                    <Sparkles className="absolute -top-2 -right-2 w-8 h-8 text-yellow-300 animate-pulse" />
                    <Sparkles className="absolute -bottom-1 -left-3 w-6 h-6 text-yellow-300 animate-pulse delay-150" />
                  </div>
                </div>

                {/* Headline */}
                <div className="text-center mb-6">
                  <h2 className="text-3xl md:text-4xl font-black text-white mb-2">
                    GET 10% OFF
                  </h2>
                  <p className="text-xl text-yellow-300 font-bold">
                    Your First Order!
                  </p>
                </div>

                {/* Description */}
                <p className="text-center text-white/90 mb-8 text-lg">
                  Join the Adanos family and receive an exclusive discount on your first online order. 
                  Plus, get updates on new menu items and special offers!
                </p>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      required
                      className="w-full pl-12 pr-4 py-4 rounded-full bg-white text-gray-900 placeholder-gray-500 font-medium focus:outline-none focus:ring-4 focus:ring-yellow-400/50 transition-all"
                      data-testid="email-popup-input"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    className="w-full py-4 bg-yellow-400 hover:bg-yellow-300 text-red-700 font-black text-lg rounded-full transition-all hover:scale-105 hover:shadow-lg active:scale-95"
                    data-testid="email-popup-submit"
                  >
                    CLAIM MY 10% OFF
                  </button>
                </form>

                {/* Footer */}
                <p className="text-center text-white/60 text-sm mt-6">
                  No spam, ever. Unsubscribe anytime.
                </p>
              </>
            ) : (
              /* Success State */
              <div className="text-center py-6">
                <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                
                <h2 className="text-3xl font-black text-white mb-4">
                  You're In! 🎉
                </h2>
                
                <p className="text-white/90 text-lg mb-6">
                  Your exclusive discount code is:
                </p>
                
                <div className="bg-white rounded-2xl p-6 mb-6">
                  <p className="text-4xl font-black text-red-600 tracking-widest">
                    WELCOME10
                  </p>
                  <p className="text-gray-600 mt-2">
                    Use at checkout for 10% off
                  </p>
                </div>
                
                <button
                  onClick={handleClose}
                  className="px-8 py-3 bg-yellow-400 hover:bg-yellow-300 text-red-700 font-bold rounded-full transition-all hover:scale-105"
                >
                  Start Shopping
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailPopup;
